import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  getCustomData,
  ICustomData,
  ICustomDataQueryParams,
} from '~/src/dash/apiV2/inventory-rules/custom-data';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { formatDataIntoLVTRows } from '~/src/dash/components/tables/IDNameTable';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { InventoryRulesModal } from '~/src/dash/views/inventory-rules-v2/InventoryRulesModal/InventoryRulesModal';
import { customDataListConfig } from '~/src/dash/views/inventory-rules-v2/CustomData/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const CustomDataTable = (): JSX.Element => {
  const { isSuperUser, session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const cfg = customDataListConfig;
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns),
  );

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<ICustomData[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string>((cfg.defaultState.orderBy as string) || 'ID');
  const [page, setPage] = useState<number>(cfg.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.defaultState.rowsPerPage || 10);
  const tableAbortControllerRef = useRef<AbortController | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [activeCustomData, setActiveCustomData] = useState<ICustomData | undefined>(undefined);
  const [isInventoryRulesModalActive, setIsInventoryRulesModalActive] = useState(false);

  const isMounted = useRef(false);

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setQuery(text);
      setPage(0);
    });
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
    cfg.activeColumnKeys = keys;
  };

  const handleClickOnInventoryRulesList = (id: string): void => {
    const selectedCustomData = responseData.find(
      (customData: ICustomData) => customData.ID.toString() === id.toString(),
    );
    unstable_batchedUpdates(() => {
      setActiveCustomData(selectedCustomData);
      setIsInventoryRulesModalActive(true);
    });
  };

  const handleCloseModals = (): void => {
    unstable_batchedUpdates(() => {
      setActiveCustomData(undefined);
      setIsInventoryRulesModalActive(false);
    });
  };

  const fetchTableData = useCallback((): void => {
    if (tableAbortControllerRef.current) {
      tableAbortControllerRef.current.abort();
    }

    tableAbortControllerRef.current = new AbortController();
    const currentController = tableAbortControllerRef.current;

    setLoadState(LoadingStateLoading);

    const tableQuery = {
      Search: query,
      Page: page,
      PageSize: rowsPerPage,
      OrderBy: orderBy,
      OrderDirection: order,
    } as ICustomDataQueryParams;

    getCustomData(session.publisher.ID, tableQuery, currentController.signal)
      .then((response) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setResponseData(response.InvRuleCustomsData || []);
          setTotalRowCount(response.TotalCount || 0);
        });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') {
          return;
        }
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setTotalRowCount(0);
        });
        console.error(err);
      });
  }, [session.publisher.ID, order, orderBy, rowsPerPage, page, query]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      fetchTableData();
    }
  }, [fetchTableData]);

  const activeColumnsList = useMemo(
    () => extractVisibleColumns(cfg.columns, activeColumnKeys, isSuperUser),
    [],
  );

  const rows = useMemo(
    () => formatDataIntoLVTRows(responseData, cfg.columns),
    [responseData, cfg.columns],
  );

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel highlighted basePadding>
        <Rows>
          <Row noPadding>
            <Cols
              wrap
              gap={spacing.medium}
              style={{
                alignItems: 'center',
                rowGap: spacing.small,
                justifyContent: 'space-between',
              }}
            >
              <Cols gap={spacing.medium} style={{ alignItems: 'center', rowGap: spacing.small }}>
                <Col>
                  <TextInput
                    enableClear
                    handleOnChange={handleOnChangeQuery}
                    msDelay={500}
                    placeholder={t('SearchByName')}
                    prefix='search'
                  />
                </Col>
                <Col>
                  <SearchResultsText
                    numRowsDisplayed={responseData.length}
                    numTotalRows={totalRowCount}
                  />
                </Col>
              </Cols>
              <Col>
                <ColumnSelector
                  preselectedColumnKeys={activeColumnKeys}
                  resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                  columnsList={activeColumnsList}
                  columnsHandleOnChangeOption={handleChangeActiveColumns}
                  columnsHandleOnAccept={handleAcceptActiveColumns}
                />
              </Col>
            </Cols>
          </Row>
          <Row noHorizontalPadding verticalPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.columns}
              customFunctions={{
                handleClickOnInventoryRulesList,
              }}
              displayNameWithID
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              tableName={t('CustomData')}
              totalRowCount={totalRowCount}
              className={cfg.cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <InventoryRulesModal
        open={isInventoryRulesModalActive}
        onClose={handleCloseModals}
        inventoryRules={activeCustomData?.InventoryRules ?? null}
        descriptionTKey='InventoryRulesCustomDataModalDescription'
      />
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          unstable_batchedUpdates(() => {
            setSnackbarMessage('');
            setSnackbarError(false);
          });
        }}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
